/**
 * SEO component that queries for data with
 * Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/how-to/querying-data/use-static-query/
 */

import * as React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { useLocation } from '@reach/router'

import ogImageTablets from '../images/ayvakit-logo-og-tablets.png'
import ogImage25MgTablets from '../images/ayvakit-logo-og-25mgtablets.png'

function Seo ({ description, title, children }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            name
            siteUrl
            og {
              siteName
            }
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = site.siteMetadata?.title

  const gatewaySchema = {
    '@context': 'https://schema.org/',
    '@type': 'MedicalWebPage',
    url: 'https://www.ayvakithcp.com/',
    audience: 'https://health-lifesci.schema.org/Clinician',
    creator: {
      '@type': 'Organization',
      name: 'Blueprint Medicines Corporation',
      url: 'https://www.blueprintmedicines.com/',
      logo: 'https://www.blueprintmedicines.com/wp-content/themes/Volum8WP/dist/img/logos/bp-logo.svg'
    },
    specialty: 'https://health-lifesci.schema.org/Hematologic',
    mainEntity: {
      '@type': 'Drug',
      name: 'AYVAKIT® (avapritinib)',
      brand: {
        '@type': 'Brand',
        name: 'AYVAKIT® (avapritinib)',
        logo: 'https://www.ayvakithcp.com/wp-content/uploads/logo-ayvakit-marquee.png',
        sameAs: 'https://en.wikipedia.org/wiki/Avapritinib'
      },
      activeIngredient: 'avapritinib',
      administrationRoute: 'Oral ',
      isProprietary: 'True',
      isAvailableGenerically: 'False',
      nonProprietaryName: 'avapritinib',
      relevantSpecialty: 'https://health-lifesci.schema.org/Hematologic',
      manufacturer: {
        '@type': 'Organization',
        name: 'Blueprint Medicines Corporation',
        url: 'https://www.blueprintmedicines.com/',
        logo: 'https://www.blueprintmedicines.com/wp-content/themes/Volum8WP/dist/img/logos/bp-logo.svg'
      },
      description:
        'AYVAKIT® (avapritinib) – a treatment option approved for the treatment of multiple indications. See ISI and PI for more information.',
      dosageForm: 'tablet ',
      drugUnit: '25 mg, 50 mg, 100 mg, 200 mg, 300 mg',
      interactingDrug: {
        '@type': 'Drug',
        name: 'Strong and Moderate CYP3A Inhibitors: Avoid coadministration of AYVAKIT with strong and moderate CYP3A inhibitors. If coadministration of AYVAKIT with a moderate inhibitor cannot be avoided, reduce dose of AYVAKIT.'
      },
      doseSchedule: [
        {
          '@type': 'DoseSchedule',
          doseUnit: 'mg',
          doseValue: '300',
          frequency: 'once daily',
          targetPopulation: 'Adults '
        },
        {
          '@type': 'DoseSchedule',
          doseUnit: 'mg',
          doseValue: '200',
          frequency: 'once daily',
          targetPopulation: 'Adults '
        },
        {
          '@type': 'DoseSchedule',
          doseUnit: 'mg',
          doseValue: '100',
          frequency: 'once daily',
          targetPopulation: 'Adults '
        },
        {
          '@type': 'DoseSchedule',
          doseUnit: 'mg',
          doseValue: '50',
          frequency: 'once daily',
          targetPopulation: 'Adults '
        },
        {
          '@type': 'DoseSchedule',
          doseUnit: 'mg',
          doseValue: '25',
          frequency: 'once daily',
          targetPopulation: 'Adults '
        }
      ],
      labelDetails:
        'https://www.blueprintmedicines.com/wp-content/uploads/uspi/AYVAKIT.pdf',
      mechanismOfAction:
        'Avapritinib is a tyrosine kinase inhibitor that targets KIT D816V, PDGFRA and PDGFRA D842 mutants as well as multiple KIT exon 11, 11/17, and 17 mutants with half maximal inhibitory concentrations (IC50s) less than 25 nM in biochemical assays. Certain mutations in PDGFRA and KIT can result in the autophosphorylation and constitutive activation of these receptors which can contribute to tumor and mast cell proliferation. Other potential targets for avapritinib include wild type KIT, PDGFRB, and CSFR1.',
      prescriptionStatus: 'https://health-lifesci.schema.org/PrescriptionOnly',
      prescribingInfo:
        'https://www.blueprintmedicines.com/wp-content/uploads/uspi/AYVAKIT.pdf',
      breastfeedingWarning:
        'Before taking AYVAKIT, tell your healthcare provider about all of your medical conditions, including if you are breastfeeding or plan to breastfeed. It is not known if AYVAKIT passes into your breast milk. Do not breastfeed during treatment with AYVAKIT and for at least 2 weeks after the final dose of AYVAKIT. Talk to your healthcare provider about the best way to feed your baby during this time.',
      pregnancyWarning:
        'AYVAKIT can cause fetal harm when administered to a pregnant woman. Advise pregnant women of the potential risk to a fetus. Advise females and males of reproductive potential to use an effective contraception during treatment with AYVAKIT and for 6 weeks after the final dose. Advise women not to breastfeed during treatment with AYVAKIT and for 2 weeks after the final dose. '
    },
    about: {
      '@type': 'MedicalCondition',
      name: 'Indolent Systemic Mastocytosis, Advanced Systemic Mastocytosis, PDGFRA GIST',
      relevantSpecialty: 'https://health-lifesci.schema.org/Hematologic',
      alternateName: 'ISM, AdvSM, PDGRFRA GIST',
      signOrSymptom: {
        '@type': 'MedicalSymptom'
      },
      associatedAnatomy: {
        '@type': 'AnatomicalStructure',
        name: 'systemic, musculoskeletal, liver, spleen, neurologic, cardiovascular, skin, gastrointestinal'
      },
      possibleTreatment: {
        '@type': 'MedicalTherapy',
        name: 'AYVAKIT®'
      }
    }
  }
  const ismSchema = {
    '@context': 'https://schema.org/',
    '@type': 'MedicalWebPage',
    url: 'https://www.ayvakithcp.com/ism/',
    audience: 'https://health-lifesci.schema.org/Clinician',
    creator: {
      '@type': 'Organization',
      name: 'Blueprint Medicines Corporation',
      url: 'https://www.blueprintmedicines.com/',
      logo: 'https://www.blueprintmedicines.com/wp-content/themes/Volum8WP/dist/img/logos/bp-logo.svg'
    },
    specialty: 'https://health-lifesci.schema.org/Hematologic',
    mainEntity: {
      '@type': 'Drug',
      name: 'AYVAKIT® (avapritinib)',
      brand: {
        '@type': 'Brand',
        name: 'AYVAKIT® (avapritinib)',
        logo: 'https://www.ayvakithcp.com/wp-content/uploads/logo-ayvakit-marquee.png',
        sameAs: 'https://en.wikipedia.org/wiki/Avapritinib'
      },
      activeIngredient: 'avapritinib',
      administrationRoute: 'Oral ',
      isProprietary: 'True',
      isAvailableGenerically: 'False',
      nonProprietaryName: 'avapritinib',
      relevantSpecialty: 'https://health-lifesci.schema.org/Hematologic',
      manufacturer: {
        '@type': 'Organization',
        name: 'Blueprint Medicines Corporation',
        url: 'https://www.blueprintmedicines.com/',
        logo: 'https://www.blueprintmedicines.com/wp-content/themes/Volum8WP/dist/img/logos/bp-logo.svg'
      },
      description:
        'AYVAKIT® (avapritinib)—a targeted therapy for selective inhibition of KIT D816V in indolent systemic mastocytosis. See ISI and PI for more information.',
      dosageForm: 'tablet ',
      drugUnit: '25 mg tablet',
      interactingDrug: {
        '@type': 'Drug',
        name: 'Strong and Moderate CYP3A Inhibitors: Avoid coadministration of AYVAKIT with strong and moderate CYP3A inhibitors. If coadministration of AYVAKIT with a moderate inhibitor cannot be avoided, reduce dose of AYVAKIT.'
      },
      doseSchedule: {
        '@type': 'DoseSchedule',
        doseUnit: 'mg',
        doseValue: '25',
        frequency: 'once daily',
        targetPopulation: 'Adults '
      },
      labelDetails:
        'https://www.blueprintmedicines.com/wp-content/uploads/uspi/AYVAKIT.pdf',
      mechanismOfAction:
        'Avapritinib is a tyrosine kinase inhibitor that targets KIT D816V, PDGFRA and PDGFRA D842 mutants as well as multiple KIT exon 11, 11/17, and 17 mutants with half maximal inhibitory concentrations (IC50s) less than 25 nM in biochemical assays. Certain mutations in PDGFRA and KIT can result in the autophosphorylation and constitutive activation of these receptors which can contribute to tumor and mast cell proliferation. Other potential targets for avapritinib include wild type KIT, PDGFRB, and CSFR1.',
      prescriptionStatus: 'https://health-lifesci.schema.org/PrescriptionOnly',
      prescribingInfo:
        'https://www.blueprintmedicines.com/wp-content/uploads/uspi/AYVAKIT.pdf',
      breastfeedingWarning:
        'Before taking AYVAKIT, tell your healthcare provider about all of your medical conditions, including if you are breastfeeding or plan to breastfeed. It is not known if AYVAKIT passes into your breast milk. Do not breastfeed during treatment with AYVAKIT and for at least 2 weeks after the final dose of AYVAKIT. Talk to your healthcare provider about the best way to feed your baby during this time.',
      pregnancyWarning:
        'AYVAKIT can cause fetal harm when administered to a pregnant woman. Advise pregnant women of the potential risk to a fetus. Advise females and males of reproductive potential to use an effective contraception during treatment with AYVAKIT and for 6 weeks after the final dose. Advise women not to breastfeed during treatment with AYVAKIT and for 2 weeks after the final dose. '
    },
    about: {
      '@type': 'MedicalCondition',
      name: 'Indolent Systemic Mastocytosis',
      sameAs: 'n/a',
      relevantSpecialty: 'https://health-lifesci.schema.org/Hematologic',
      alternateName: 'ISM',
      signOrSymptom: {
        '@type': 'MedicalSymptom'
      },
      associatedAnatomy: {
        '@type': 'AnatomicalStructure',
        name: 'systemic, musculoskeletal, liver, spleen, neurologic, cardiovascular, skin, gastrointestinal'
      },
      possibleTreatment: {
        '@type': 'MedicalTherapy',
        name: 'AYVAKIT®'
      }
    }
  }
  const advsmSchema = {
    '@context': 'https://schema.org/',
    '@type': 'MedicalWebPage',
    url: 'https://www.ayvakithcp.com/ism/',
    audience: 'https://health-lifesci.schema.org/Clinician',

    mainEntity: {
      '@type': 'MedicalTherapy',
      name: 'AYVAKIT® (avapritinib)',
      drug: {
        '@type': 'Drug',
        name: 'AYVAKIT® (avapritinib)',
        brand: {
          '@type': 'Brand',
          name: 'AYVAKIT® (avapritinib)',
          logo: 'https://www.ayvakithcp.com/wp-content/uploads/logo-ayvakit-marquee.png'
        }
      },
      adverseOutcome: [
        {
          '@type': 'MedicalEntity',
          name: 'Edema'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Nausea'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Fatigue/asthenia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Pyrexia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cognitive effects'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Vomiting'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Diarrhea'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Dyspepsia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Sleep disorders'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Taste effects'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Mood disorders'
        },
        {
          '@type': 'MedicalEntity',
          name: ' Decreased appetite'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Alopecia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Pleural effusion'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Weight decreased'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hypertension'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Thyroid disorders'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Palmar plantar erythrodysesthesia'
        },
        {
          '@type': 'MedicalEntity',
          name: ' Arthralgia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Epistaxis'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cardiac failure'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cardiac failure congestive'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Ascites'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hemorrhage'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Gastrointestinal hemorrhage'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Large intestine perforation'
        },
        {
          '@type': 'MedicalEntity',
          name: ' Cholelithiasis'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Upper respiratory tract infection'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Urinary tract infection'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Herpes zoster'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Flushing, Pain in extremity'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cough'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Pruritus'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Decreased phosphate'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Face edema'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Liver transaminase increased'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Insomnia, Hematoma'
        },
        {
          '@type': 'MedicalEntity',
          name: ' Blood alkaline phosphatase increased'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Photosensitivity'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Decreased lacrimation'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Abdominal pain'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Constipation'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Rash'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Dizziness'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hair color changes'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Eye edema'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Peripheral edema'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Headache'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hypotension'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hot flush'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Increased lacrimation'
        }
      ],
      seriousAdverseOutcome: [
        {
          '@type': 'MedicalEntity',
          name: 'Intracranial hemorrhage'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cognitive effects'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Embryo-fetal toxicity'
        }
      ]
    }
  }
  const adverseOutcomesSchema = {
    '@context': 'https://schema.org/',
    '@type': 'MedicalWebPage',
    url: 'https://www.ayvakithcp.com/ism/',
    audience: 'https://health-lifesci.schema.org/Clinician',

    mainEntity: {
      '@type': 'MedicalTherapy',
      name: 'AYVAKIT® (avapritinib)',
      drug: {
        '@type': 'Drug',
        name: 'AYVAKIT® (avapritinib)',
        brand: {
          '@type': 'Brand',
          name: 'AYVAKIT® (avapritinib)',
          logo: 'https://www.ayvakithcp.com/wp-content/uploads/logo-ayvakit-marquee.png'
        }
      },
      adverseOutcome: [
        {
          '@type': 'MedicalEntity',
          name: 'Edema'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Nausea'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Fatigue/asthenia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Pyrexia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cognitive effects'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Vomiting'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Diarrhea'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Dyspepsia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Sleep disorders'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Taste effects'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Mood disorders'
        },
        {
          '@type': 'MedicalEntity',
          name: ' Decreased appetite'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Alopecia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Pleural effusion'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Weight decreased'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hypertension'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Thyroid disorders'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Palmar plantar erythrodysesthesia'
        },
        {
          '@type': 'MedicalEntity',
          name: ' Arthralgia'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Epistaxis'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cardiac failure'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cardiac failure congestive'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Ascites'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hemorrhage'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Gastrointestinal hemorrhage'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Large intestine perforation'
        },
        {
          '@type': 'MedicalEntity',
          name: ' Cholelithiasis'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Upper respiratory tract infection'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Urinary tract infection'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Herpes zoster'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Flushing, Pain in extremity'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cough'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Pruritus'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Decreased phosphate'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Face edema'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Liver transaminase increased'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Insomnia, Hematoma'
        },
        {
          '@type': 'MedicalEntity',
          name: ' Blood alkaline phosphatase increased'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Photosensitivity'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Decreased lacrimation'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Abdominal pain'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Constipation'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Rash'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Dizziness'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hair color changes'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Eye edema'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Peripheral edema'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Headache'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hypotension'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Hot flush'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Increased lacrimation'
        }
      ],
      seriousAdverseOutcome: [
        {
          '@type': 'MedicalEntity',
          name: 'Intracranial hemorrhage'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Cognitive effects'
        },
        {
          '@type': 'MedicalEntity',
          name: 'Embryo-fetal toxicity'
        }
      ]
    }
  }

  const gatewaySchemaAsString = JSON.stringify(gatewaySchema, null, 2)
  const ismSchemaAsString = JSON.stringify(ismSchema, null, 2)
  const advsmSchemaAsString = JSON.stringify(advsmSchema, null, 2)
  const adverseOutcomesSchemaAsString = JSON.stringify(
    adverseOutcomesSchema,
    null,
    2
  )

  const HandleSchema = () => {
    const location = useLocation()
    if (location.pathname.indexOf('ism/safety') > -1) {
      return (
        <script type='application/ld+json'>
          {adverseOutcomesSchemaAsString}
        </script>
      )
    }
    if (location.pathname.indexOf('ism') > -1) {
      return <script type='application/ld+json'>{ismSchemaAsString}</script>
    }
    if (location.pathname.indexOf('advsm') > -1) {
      return <script type='application/ld+json'>{advsmSchemaAsString}</script>
    }
    if (location.pathname.indexOf('gist-hcp') > -1) {
      return console.log('this is the gist page, no schema provided for gist')
    } else {
      return <script type='application/ld+json'>{gatewaySchemaAsString}</script>
    }
  }

  const HandleOgImage = () => {
    const location = useLocation()
    if (location.pathname == '/') {
      return (
        <meta property='og:image' content={location.origin + ogImageTablets} />
      )
    } else {
      return (
        <meta
          property='og:image'
          content={location.origin + ogImage25MgTablets}
        />
      )
    }
  }

  const HandleHreflangAndCanonicalTag = () => {
    const location = useLocation()
    let hrefValue = 'https://www.ayvakithcp.com' + location.pathname

    return (
      <>
        <link rel='alternate' hrefLang='en-us' href={hrefValue}></link>
        <link rel='canonical' href={hrefValue}></link>
      </>
    )
  }

  const HandleOGUrl = () => {
    const location = useLocation()
    let hrefValue = 'https://www.ayvakithcp.com' + location.pathname

    return (
      <>
        <meta property="og:url" content={hrefValue} />
      </>
    )
  }

  return (
    <>
      <title>{defaultTitle ? `${title}` : defaultTitle}</title>
      <meta name='description' content={metaDescription} />
      <meta property='og:title' content={title} />
      <meta property='og:description' content={metaDescription} />
      <meta property='og:type' content='website' />
      {HandleOGUrl()}
      <meta property='og:site_name' content={site.siteMetadata?.og?.siteName} />
      <meta name='twitter:card' content='summary' />
      <meta name='twitter:creator' content={site.siteMetadata?.author || ``} />
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={metaDescription} />
      {HandleOgImage()}
      {HandleHreflangAndCanonicalTag()}
      {HandleSchema()}
      {children}
    </>
  )
}

export default Seo
